import React from 'react'
import { StandardColumn } from '../../../../components/StandardPage'
import { OverflowCell } from '../../../../components/OverflowCell'
import { Product, Script } from '../AddProduct/types'
import { formatCurrency } from '../ViewProduct/utils'
import { StyledSelect } from './style'
import ItemsDisplay from './ItemsDisplay'

export const productFinalScreenKey = 'product_final'

export const getSelectedScript = (
  product: Product,
  selectedScriptIds: Record<number, number>,
): Script | null => {
  const selectedId = product.id ? selectedScriptIds[product.id] : undefined
  return (
    product.scripts?.find((script) => script.id === selectedId) ||
    product.scripts?.[0] ||
    null
  )
}

export const getScriptDeadlineInDays = (script: Script | null): number => {
  if (!script) return 0
  return (script.additionalDeadline || 0) + (script.totalDeliveryTime || 0)
}

const formatItemsText = (script: Script | null): string => {
  if (!script?.items || script.items.length === 0) return 'Nenhum item'
  return script.items
    .map((item) => `${item.order}. ${item.name || 'Item sem nome'}`)
    .join(', ')
}

export const buildProductFinalColumns = (
  selectedScriptIds: Record<number, number>,
  onScriptChange: (productId: number, scriptId: number) => void,
): StandardColumn<Product>[] => [
  {
    id: 'name',
    label: 'Produto',
    accessor: (product) => product.name || '-',
    cell: (product) => <OverflowCell text={product.name || '-'} maxWidth={200} />,
  },
  {
    id: 'norm',
    label: 'Norma',
    accessor: (product) => product.norm || '-',
    cell: (product) => <OverflowCell text={product.norm || '-'} maxWidth={250} />,
  },
  {
    id: 'script',
    label: 'Script',
    sortable: false,
    accessor: (product) => getSelectedScript(product, selectedScriptIds)?.name || 'Nenhum',
    cell: (product) => {
      const selectedScript = getSelectedScript(product, selectedScriptIds)
      if (product.scripts && product.scripts.length > 1) {
        return (
          <StyledSelect
            className="custom-select"
            value={selectedScript?.id || ''}
            onChange={(event: React.ChangeEvent<HTMLSelectElement>) => {
              if (product.id) onScriptChange(product.id, Number(event.target.value))
            }}
            onClick={(event: React.MouseEvent) => event.stopPropagation()}
          >
            {product.scripts.map((script) => (
              <option key={script.id} value={script.id}>
                {script.name}
              </option>
            ))}
          </StyledSelect>
        )
      }
      return selectedScript?.name || 'Nenhum'
    },
  },
  {
    id: 'items',
    label: 'Itens',
    sortable: false,
    accessor: (product) => formatItemsText(getSelectedScript(product, selectedScriptIds)),
    cell: (product) => {
      const items = getSelectedScript(product, selectedScriptIds)?.items
      if (!items || items.length === 0) return 'Nenhum item'
      return <ItemsDisplay items={items} maxWidth={200} />
    },
  },
  {
    id: 'deadline',
    label: 'Prazo (dias)',
    accessor: (product) => getScriptDeadlineInDays(getSelectedScript(product, selectedScriptIds)),
  },
  {
    id: 'cost',
    label: 'Custo (últ.)',
    accessor: (product) => getSelectedScript(product, selectedScriptIds)?.cost || 0,
    cell: (product) => formatCurrency(getSelectedScript(product, selectedScriptIds)?.cost || 0),
    exportValue: (product) => getSelectedScript(product, selectedScriptIds)?.cost || 0,
  },
  {
    id: 'price',
    label: 'Preço (últ)',
    accessor: (product) => (getSelectedScript(product, selectedScriptIds)?.cost || 0) * 1.3,
    cell: (product) => formatCurrency((getSelectedScript(product, selectedScriptIds)?.cost || 0) * 1.3),
    exportValue: (product) => (getSelectedScript(product, selectedScriptIds)?.cost || 0) * 1.3,
  },
]
